import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  ErrorLabel,
  Password,
} from '@ibsys/kit';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/entities/auth';
import { useSetOwnPassword } from '@/entities/users';

type FormValues = {
  newPassword: string;
  confirmPassword: string;
};

/**
 * Модальное окно установки постоянного пароля.
 * Открывается, если в ответе на /auth/login вернулся флаг isTempPassword=true.
 * Закрытие по клику вне окна и по Esc заблокировано — обязательное действие.
 */
export const TemporaryPasswordModal: FC = () => {
  const { t } = useTranslation('auth');
  const isTempPassword = useAuthStore((s) => s.isTempPassword);
  const setIsTempPassword = useAuthStore((s) => s.setIsTempPassword);
  const { mutateAsync: setOwnPassword, isPending } = useSetOwnPassword();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const submit = handleSubmit(async ({ newPassword }) => {
    setSubmitError(null);
    try {
      await setOwnPassword({ password: newPassword });
      // Закрываем модалку и пускаем пользователя в основной интерфейс
      setIsTempPassword(false);
      reset();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('tempPasswordModal.errorGeneric'));
    }
  });

  return (
    <DialogRoot open={isTempPassword} modal>
      <DialogContent
        showCloseButton={false}
        className="max-w-96"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('tempPasswordModal.title')}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <p className="text-sm text-foreground">{t('tempPasswordModal.description')}</p>
          <Password
            control={control}
            name="newPassword"
            label={t('tempPasswordModal.newPassword')}
            autoComplete="new-password"
            rules={{
              required: { value: true, message: t('tempPasswordModal.errorNewPasswordEmpty') },
            }}
          />
          <Password
            control={control}
            name="confirmPassword"
            label={t('tempPasswordModal.confirmPassword')}
            autoComplete="new-password"
            rules={{
              required: { value: true, message: t('tempPasswordModal.errorConfirmPasswordEmpty') },
              validate: (value, formValues) =>
                value === formValues.newPassword || t('tempPasswordModal.errorPasswordsDoNotMatch'),
            }}
          />
          {submitError && <ErrorLabel className="text-center">{submitError}</ErrorLabel>}
          <DialogFooter>
            <Button type="submit" className="w-full" isLoading={isPending}>
              {t('tempPasswordModal.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
